import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import { useHirerAuth } from '@seek/hirer-auth-react';
import { useAccountContext } from '@seek/hirer-graphql-react';
import { Box, Page } from 'braid-design-system';
import { useEffect, type ReactNode } from 'react';

import { Helmet } from 'src/components/Helmet/Helmet';
import { ScrollToTop } from 'src/components/ScrollToTop/ScrollToTop';
import {
  consumerTrackingFunction,
  linkRenderer,
} from 'src/components/Tracking/linkRenderer';
import { analytics } from 'src/utils/analytics/analytics';
import { useConfig } from 'src/utils/hooks/useContext';

const LOGGED_IN = 'L+I';
const LOGGED_OUT = 'L+O';

interface PageTrackingProps {
  page: string;
  eventName: string;
  siteSection?: string;
}

interface Props {
  children: ReactNode;
  title: string;
  scroll?: boolean;
  pageTrackingProps: PageTrackingProps;
  footerPosition?: 'belowFold';
}

export const PageWrapper = ({
  pageTrackingProps,
  title,
  scroll = true,
  children,
  footerPosition,
}: Props) => {
  const { language, environment, site } = useConfig();
  const { authenticated, loading } = useHirerAuth();
  const { accountContext, error } = useAccountContext();

  const headerFooterProps = {
    site,
    locale: language,
    isStaging: environment === 'development',
  };

  let headerFooterContext;
  if (!loading) {
    headerFooterContext = authenticated
      ? accountContext?.headerFooterContext
      : { identityContext: null }; // this is required to explicitly render the signed-out header state rather than undetermined.
  }

  useEffect(() => {
    if (loading) {
      const loginStatus = authenticated ? LOGGED_IN : LOGGED_OUT;
      const authTrackingProps = {
        loginStatus,
        userId: accountContext?.currentUser?.id,
        seekAdvertiserId: accountContext?.currentAdvertiser?.billingId,
      };

      const combinedProps = {
        ...pageTrackingProps,
        ...authTrackingProps,
      };

      if (typeof window !== undefined) {
        window.currentPageTrackingData = combinedProps;
      }

      analytics.view(pageTrackingProps.eventName);
    }
  }, [authenticated, accountContext, pageTrackingProps, loading]);

  return (
    <Page
      footer={
        // Using Google Translate on the footer seems to crash React
        // TODO: Find a better fix and upstream it
        <div translate="no">
          <EmployerFooter
            {...headerFooterProps}
            {...headerFooterContext}
            linkRenderer={linkRenderer('global-footer')}
          />
        </div>
      }
      footerPosition={footerPosition}
    >
      <EmployerHeader
        linkRenderer={linkRenderer('global-header')}
        onToggleButtonClick={consumerTrackingFunction('global-header')}
        {...headerFooterProps}
        {...headerFooterContext}
        error={error}
      />
      {scroll && <ScrollToTop />}
      <Helmet title={title} />
      <Box>{children}</Box>
    </Page>
  );
};
